/** Staff CFO & Advisory workspace for one client. Every figure shown is a display string computed by the server's formula library. */
import { canAccessModule, DSO_BASIS_TEXT, Exact, FORECAST_RUNWAY_LABEL, formatCents, INDUSTRIES, KPI_DEFINITIONS, KPI_ORDER, monthsUntilCashRunsOut, NOT_MEANINGFUL_HELP, parseDollars, type DsoBasis, type KpiKey, type Variance } from "@clientflow/shared";
import { ask } from "@/components/dialogs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Circle, RefreshCw, X } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { Benchmarks } from "@/components/benchmarks";
import { FinancialStatements } from "@/components/financial-statements";
import { Empty, Loading, Section, Segmented } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime, plural } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const monthLabel = (p: string) => new Date(`${p}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

/** Dollars typed by staff → cents, with a readable error. */
function cents(v: string, what: string): number {
  try {
    return parseDollars(v);
  } catch {
    throw new ApiError(400, "invalid_amount", `${what}: enter dollars and cents, like 1,234.56`);
  }
}

const LIGHT: Record<string, string> = {
  green: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
  amber: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  red: "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200",
  none: "",
};

type ScoreRow = { key: KpiKey; label: string; formula: string; target: { comparator: string; target: string; tolerance: string } | null; values: Array<{ period: string; display: string; note: string | null; light: string }> };
type CfoView = {
  profile: { debtServiceMonthlyCents: number | null; headcountOverride: number | null; noneCategories: string[]; industry: string | null; reportDraftDay: number | null; reportPublishDay: number | null; fiscalYearStartMonth: number | null; dsoBasis: DsoBasis } | null;
  /** Which sales DSO uses, in plain words. */
  dsoBasis: { basis: DsoBasis; label: string; sentence: string };
  quickbooksFiscalYearStartMonth: number | null;
  mapping: Array<{ category: string; qboAccountId: string }>;
  accounts: Array<{ qboId: string; name: string; type: string; subType: string | null; active: boolean }>;
  snapshots: Array<{ period: string; quality: "final" | "preliminary"; qualityReasons: string[]; asOf: string }>;
  scorecard: ScoreRow[];
  alerts: Array<{ id: string; period: string; severity: "warning" | "critical"; message: string }>;
  suggestedPeriod: string;
};

const PerformanceDashboard = lazy(() => import("./performance-dashboard"));
const PlanWorkspace = lazy(() => import("./plan-workspace"));

/** The client record's CFO sections (shown nested in the client's section list). */
export const CFO_SECTIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "financials", label: "Financials" },
  { key: "planning", label: "Planning" },
  { key: "reporting", label: "Reporting" },
  { key: "settings", label: "Settings" },
];

/** Pages within a section, picked with a small switch above the content. */
const PAGES: Record<string, Array<[string, string]>> = {
  financials: [
    ["statements", "Financial Statements"],
    ["kpis", "KPIs & Targets"],
    ["benchmarks", "Benchmarks"],
    ["analysis", "Statement Analysis"],
  ],
  planning: [
    ["plans", "Plans & Scenarios"],
    ["budget", "Budget"],
    ["quick", "Quick Forecasts"],
  ],
  reporting: [
    ["report", "Monthly Report"],
    ["meetings", "Meetings"],
  ],
};

/** Old tab names, so links and habits still land in the right place. */
const OLD: Record<string, [string, string?]> = {
  financials: ["financials", "statements"],
  scorecard: ["financials", "kpis"],
  benchmarks: ["financials", "benchmarks"],
  statements: ["financials", "analysis"],
  budget: ["planning", "budget"],
  forecast: ["planning", "plans"],
  report: ["reporting", "report"],
  meetings: ["reporting", "meetings"],
  setup: ["settings"],
};

function PageSwitch({ pages, value, onChange }: { pages: Array<[string, string]>; value: string; onChange: (v: string) => void }) {
  return (
    <div className="mb-4">
      <Segmented label="Page" value={value} onChange={onChange} options={pages.map(([k, l]) => ({ value: k, label: l }))} />
    </div>
  );
}

export function CfoTab({ clientId, me, section: requested, onSection }: { clientId: string; me: Me; section: string; onSection: (s: string) => void }) {
  const { data, isLoading, error } = useQuery<CfoView>({ queryKey: ["/api/clients/", clientId, "/cfo"], retry: false });
  const [mapped, mappedPage] = OLD[requested] ?? [requested];
  const section = CFO_SECTIONS.some((x) => x.key === mapped) ? mapped : "dashboard";
  const [pages, setPages] = useState<Record<string, string>>(mappedPage ? { [section]: mappedPage } : {});
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const page = pages[section] ?? PAGES[section]?.[0]?.[0];
  const openSettings = () => onSection("settings");
  return (
    <div>
      {PAGES[section] && <PageSwitch pages={PAGES[section]!} value={page!} onChange={(v) => setPages({ ...pages, [section]: v })} />}
      {section === "dashboard" && (
        <Suspense fallback={<Loading tall />}>
          <PerformanceDashboard
            endpoint={`/api/clients/${clientId}/cfo/dashboard`}
            audience="staff"
            clientId={clientId}
            canRefresh={hasPermission(me, "engagements.write")}
            canDismiss={hasPermission(me, "clients.write")}
            canPlan={hasPermission(me, "engagements.write")}
            showOwnerTax={canAccessModule(me.role, me.staffModules, "tax")}
            onOpenSettings={openSettings}
          />
        </Suspense>
      )}
      {section === "financials" && page === "statements" && <FinancialStatements clientId={clientId} />}
      {section === "financials" && page === "kpis" && (
        <div className="space-y-6">
          <Scorecard clientId={clientId} me={me} data={data} />
          <Targets clientId={clientId} me={me} scorecard={data.scorecard} />
        </div>
      )}
      {section === "financials" && page === "benchmarks" && <Benchmarks clientId={clientId} onOpenSettings={openSettings} />}
      {section === "financials" && page === "analysis" && <StatementCheck clientId={clientId} me={me} />}
      {section === "planning" && page === "plans" && (
        <Suspense fallback={<Loading />}>
          <PlanWorkspace clientId={clientId} me={me} />
        </Suspense>
      )}
      {section === "planning" && page === "budget" && <Budgets clientId={clientId} me={me} />}
      {section === "planning" && page === "quick" && (
        <Section title="Quick forecasts" description="A 12-month P&L forecast from the last three months, and the 13-week cash forecast.">
          <Forecasts clientId={clientId} me={me} />
        </Section>
      )}
      {section === "reporting" && page === "report" && <MonthlyReport clientId={clientId} me={me} snapshots={data.snapshots} />}
      {section === "reporting" && page === "meetings" && <Meetings clientId={clientId} me={me} />}
      {section === "settings" && (
        <>
          <p className="mb-3 text-sm text-muted-foreground">Account mapping, KPI inputs, fiscal year and the monthly report schedule for this client. These control how the dashboard's figures are calculated.</p>
          <Setup clientId={clientId} me={me} data={data} />
        </>
      )}
    </div>
  );
}

// --- Scorecard ------------------------------------------------------------------------------

function Scorecard({ clientId, me, data }: { clientId: string; me: Me; data: CfoView }) {
  const { toast } = useToast();
  const [from, setFrom] = useState(data.snapshots[0]?.period ?? data.suggestedPeriod);
  const [to, setTo] = useState(data.suggestedPeriod);
  const run = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ computed: Array<{ period: string; quality: string }> }>("POST", `/api/clients/${clientId}/cfo/snapshots`, { from, to }),
    onSuccess: (r) => toast({ title: `Scorecard updated for ${r.computed.length} month${r.computed.length > 1 ? "s" : ""}` }),
  });
  const dismiss = useMutation({ mutationFn: (id: string) => api.send("POST", `/api/cfo/alerts/${id}/dismiss`) });
  const canRun = hasPermission(me, "engagements.write");
  const periods = data.snapshots;
  return (
    <div className="space-y-6">
      {data.alerts.length > 0 && (
        <details className="rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/10">
          <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> KPI exceptions ({data.alerts.length})
          </summary>
        <ul className="mt-2 space-y-2">
          {data.alerts.map((a) => (
            <li key={a.id} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${a.severity === "critical" ? "border-destructive/40 bg-destructive/5" : "border-amber-300 bg-amber-50 dark:bg-amber-900/20"}`}>
              <AlertTriangle className={`mt-0.5 h-4 w-4 ${a.severity === "critical" ? "text-destructive" : "text-amber-600"}`} />
              <span className="flex-1">
                <span className="text-xs text-muted-foreground">{monthLabel(a.period)} · </span>
                {a.message}
              </span>
              <button aria-label="Dismiss exception" title="Dismiss" onClick={() => dismiss.mutate(a.id)}>
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
        </details>
      )}
      <Section
        title="KPI scorecard"
        action={
          canRun && (
            <div className="flex flex-wrap items-end gap-2">
              <Input aria-label="From month" type="month" max={data.suggestedPeriod} value={from} onChange={(e) => setFrom(e.target.value)} className="w-36" />
              <Input aria-label="To month" type="month" max={data.suggestedPeriod} value={to} onChange={(e) => setTo(e.target.value)} className="w-36" />
              <Button size="sm" variant="outline" onClick={() => run.mutate()} disabled={run.isPending}>
                <RefreshCw className="h-4 w-4" /> Refresh from QuickBooks
              </Button>
            </div>
          )
        }
      >
        {run.error && <p className="mb-3 text-sm text-destructive">{errorMessage(run.error)}</p>}
        {!periods.length ? (
          <Empty>No months have been calculated. Complete the account mapping, then refresh a range of months from QuickBooks.</Empty>
        ) : (
          <TableScroll label="KPI scorecard table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-3">KPI</th>
                  {periods.map((p) => (
                    <th key={p.period} className="px-2 py-1.5 text-right" title={p.qualityReasons.join("; ")}>
                      {monthLabel(p.period)}
                      {p.quality === "preliminary" && <span className="block text-[10px] font-normal text-amber-700">preliminary</span>}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-right">Target</th>
                </tr>
              </thead>
              <tbody>
                {data.scorecard.map((k) => (
                  <tr key={k.key} className="border-t border-border">
                    <td className="whitespace-nowrap py-1.5 pr-4" title={k.formula}>
                      {k.label}
                    </td>
                    {k.values.map((v) => (
                      <td key={v.period} className="px-2 py-1.5 text-right tabular-nums" title={v.note ?? undefined}>
                        <span className={`rounded px-1.5 py-0.5 ${LIGHT[v.light]}`}>{v.display}</span>
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right text-xs text-muted-foreground">{k.target ? `${k.target.comparator === "at_least" ? "≥" : "≤"} ${k.target.target}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-muted-foreground">
              Figures are QuickBooks totals as of {formatDateTime(periods[periods.length - 1]!.asOf)}. Hover over a KPI for its formula, or over "—" for the reason it cannot be calculated. A month remains preliminary until its month-end close is complete.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {data.dsoBasis.sentence} Runway at current burn is cash divided by the average monthly burn of the last three months; forecasts show months until cash runs out instead.
            </p>
          </TableScroll>
        )}
      </Section>
    </div>
  );
}

// --- Setup ----------------------------------------------------------------------------------

const CATEGORIES: Array<{ key: string; label: string; help: string; kinds: string[] }> = [
  { key: "payroll", label: "Payroll", help: "Wages, salaries, payroll taxes and benefits", kinds: ["Expense", "Cost of Goods Sold", "Other Expense"] },
  { key: "interest", label: "Interest expense", help: "Interest paid on loans and cards (EBITDA adds it back)", kinds: ["Expense", "Other Expense"] },
  { key: "interest_income", label: "Interest income", help: "Interest earned on bank and savings accounts (EBITDA takes it out). If not mapped, it counts as $0.", kinds: ["Other Income", "Income"] },
  { key: "taxes", label: "Income taxes", help: "Income taxes only (not sales tax or licenses)", kinds: ["Expense", "Other Expense"] },
  { key: "depreciation_amortization", label: "Depreciation & amortization", help: "Non-cash depreciation and amortization", kinds: ["Expense", "Other Expense"] },
  { key: "fixed_cost", label: "Fixed costs", help: "Costs that do not vary with sales (rent, insurance, salaried staff)", kinds: ["Expense", "Other Expense", "Cost of Goods Sold"] },
  { key: "inventory", label: "Inventory", help: "Inventory asset accounts (QuickBooks' Inventory type is used if none are picked)", kinds: ["Other Current Asset"] },
  { key: "marketable_securities", label: "Marketable securities", help: "Investments readily convertible to cash", kinds: ["Other Current Asset", "Bank", "Other Asset"] },
  { key: "invoiced_sales", label: "Invoiced (credit) sales", help: "Income accounts for sales billed on an invoice, not paid at the time of sale. Used when DSO is based on invoiced sales.", kinds: ["Income"] },
];

const selectCls = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

function Setup({ clientId, me, data }: { clientId: string; me: Me; data: CfoView }) {
  const { toast } = useToast();
  const canEdit = hasPermission(me, "clients.write");
  const [debt, setDebt] = useState(data.profile?.debtServiceMonthlyCents == null ? "" : (data.profile.debtServiceMonthlyCents / 100).toFixed(2));
  const [headcount, setHeadcount] = useState(data.profile?.headcountOverride == null ? "" : String(data.profile.headcountOverride));
  const [none, setNone] = useState<string[]>(data.profile?.noneCategories ?? []);
  const [industry, setIndustry] = useState(data.profile?.industry ?? "");
  const [fyMonth, setFyMonth] = useState(data.profile?.fiscalYearStartMonth ?? 0);
  const [draftDay, setDraftDay] = useState(data.profile?.reportDraftDay ?? 0);
  const [publishDay, setPublishDay] = useState(data.profile?.reportPublishDay ?? 0);
  const [dsoBasis, setDsoBasis] = useState<DsoBasis>(data.profile?.dsoBasis ?? "total_revenue");
  const saveProfile = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("PUT", `/api/clients/${clientId}/cfo/profile`, {
        debtServiceMonthlyCents: debt.trim() ? cents(debt, "Monthly loan payments") : null,
        headcountOverride: headcount.trim() ? Number(headcount) : null,
        noneCategories: none,
        industry: industry || null,
        fiscalYearStartMonth: fyMonth || null,
        reportDraftDay: draftDay || null,
        reportPublishDay: publishDay || null,
        dsoBasis,
      }),
    onSuccess: () => toast({ title: "Changes saved" }),
  });
  const qbFy = data.quickbooksFiscalYearStartMonth;
  const saveMap = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { category: string; ids: string[] }) => api.send("PUT", `/api/clients/${clientId}/cfo/mapping/${v.category}`, { accountIds: v.ids }),
  });
  if (!data.accounts.length) return <Empty>Connect QuickBooks Online on the Accounting Services tab first. The chart of accounts is used for account mapping.</Empty>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section title="Account mapping">
        <p className="mb-3 text-xs text-muted-foreground">Several KPIs require the QuickBooks accounts that hold each type of cost. A parent account includes its sub-accounts. Until a category is mapped or marked "None", KPIs that depend on it show "—".</p>
        <div className="space-y-4">
          {CATEGORIES.map((c) => {
            const picked = data.mapping.filter((m) => m.category === c.key).map((m) => m.qboAccountId);
            const options = data.accounts.filter((a) => c.kinds.includes(a.type) && a.active);
            return (
              <details key={c.key} className="rounded-lg border border-border p-3">
                <summary className="cursor-pointer text-sm font-medium">
                  {c.label}{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    {picked.length ? `${picked.length} account${picked.length > 1 ? "s" : ""}` : none.includes(c.key) ? "none (counts as $0)" : "not mapped"}
                  </span>
                </summary>
                <p className="my-2 text-xs text-muted-foreground">{c.help}</p>
                <div className="grid max-h-48 gap-1 overflow-y-auto">
                  {options.map((a) => (
                    <label key={a.qboId} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={picked.includes(a.qboId)}
                        onChange={(e) => saveMap.mutate({ category: c.key, ids: e.target.checked ? [...picked, a.qboId] : picked.filter((x) => x !== a.qboId) })}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
                {!picked.length && canEdit && (
                  <label className="mt-2 flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={none.includes(c.key)} onChange={(e) => setNone(e.target.checked ? [...none, c.key] : none.filter((x) => x !== c.key))} />
                    This client has none (count as $0) — save below
                  </label>
                )}
              </details>
            );
          })}
        </div>
        {saveMap.error && <p className="mt-2 text-sm text-destructive">{errorMessage(saveMap.error)}</p>}
      </Section>
      <div className="space-y-6">
        <Section title="Supplemental data">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="cfo-debt">Monthly debt service (principal and interest)</Label>
              <Input id="cfo-debt" inputMode="decimal" placeholder="Blank if unknown; 0.00 if no debt" value={debt} onChange={(e) => setDebt(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfo-industry">Industry (for benchmarks)</Label>
              <select id="cfo-industry" className={selectCls} value={industry} onChange={(e) => setIndustry(e.target.value)} disabled={!canEdit}>
                <option value="">Not set</option>
                {Object.entries(INDUSTRIES).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfo-fy">Fiscal year starts in</Label>
              <select id="cfo-fy" className={selectCls} value={fyMonth} onChange={(e) => setFyMonth(Number(e.target.value))} disabled={!canEdit}>
                <option value={0}>As set in QuickBooks{qbFy ? ` (${MONTH_NAMES[qbFy - 1]})` : " (January)"}</option>
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Decides what "year to date" means on the dashboard, statements and report packages.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cfo-hc">Headcount (override)</Label>
              <Input id="cfo-hc" inputMode="numeric" placeholder="Blank to work it out" value={headcount} onChange={(e) => setHeadcount(e.target.value.replace(/\D/g, ""))} disabled={!canEdit} />
              <p className="text-xs text-muted-foreground">Revenue per employee uses this number when it is set. Otherwise it uses the employees paid in the month (from payroll), or the active employees in QuickBooks.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfo-dso">Days sales outstanding (DSO) is based on</Label>
              <select id="cfo-dso" className={selectCls} value={dsoBasis} onChange={(e) => setDsoBasis(e.target.value as DsoBasis)} disabled={!canEdit}>
                <option value="total_revenue">{DSO_BASIS_TEXT.total_revenue.label}</option>
                <option value="invoiced_sales">{DSO_BASIS_TEXT.invoiced_sales.label}</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Total revenue suits most businesses. For a business with significant cash sales, such as a café or a shop, choose invoiced sales only and map the invoiced sales accounts, so cash sales don't make customers look quicker to pay. Refresh the scorecard from QuickBooks after changing it.
              </p>
            </div>
          </div>
        </Section>
        <Section title="Monthly report schedule" description="Prepare the monthly CFO report automatically. Nothing reaches the client until staff approve it.">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cfo-draft-day">Draft the report</Label>
              <select id="cfo-draft-day" className={selectCls} value={draftDay} onChange={(e) => setDraftDay(Number(e.target.value))} disabled={!canEdit}>
                <option value={0}>Manually</option>
                {DAYS.map((d) => (
                  <option key={d} value={d}>
                    On the {ordinal(d)} of the month
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfo-publish-day">Publish to the client</Label>
              <select id="cfo-publish-day" className={selectCls} value={publishDay} onChange={(e) => setPublishDay(Number(e.target.value))} disabled={!canEdit}>
                <option value={0}>Manually</option>
                {DAYS.map((d) => (
                  <option key={d} value={d}>
                    On the {ordinal(d)}, once approved
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            From the draft day, once last month's close is complete, the figures are refreshed from QuickBooks, the report is drafted with AI commentary when it is turned on, and the client's team is emailed to review it. From the publish day, a report you have approved is published to the client's portal. A month whose close finishes after the month ends is drafted by hand.
          </p>
          {publishDay > 0 && draftDay > 0 && publishDay < draftDay && <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">The publish day is before the draft day: approved reports go out as soon as they are approved.</p>}
        </Section>
        {canEdit && (
          <div className="flex items-center gap-3">
            <Button onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>
              Save settings
            </Button>
            {saveProfile.error && <p className="text-sm text-destructive">{errorMessage(saveProfile.error)}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Targets({ clientId, me, scorecard }: { clientId: string; me: Me; scorecard: ScoreRow[] }) {
  const [key, setKey] = useState<KpiKey>("gross_margin");
  const [comparator, setComparator] = useState<"at_least" | "at_most">("at_least");
  const [value, setValue] = useState("");
  const kind = KPI_DEFINITIONS[key].kind;
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      // Targets are stored exactly: percent as a fraction ("40" → "0.4"), money as cents.
      const v = value.trim();
      if (!/^-?\d+(\.\d+)?$/.test(v.replace(/[$,%]/g, ""))) throw new ApiError(400, "invalid", "Enter a number");
      const plain = v.replace(/[$,%]/g, "");
      const target = kind === "percent" ? pctToFraction(plain) : kind === "money" ? String(cents(plain, "Target")) : plain;
      return api.send("PUT", `/api/clients/${clientId}/cfo/targets/${key}`, { comparator, target, tolerance: "0.10" });
    },
    onSuccess: () => setValue(""),
  });
  const remove = useMutation({ mutationFn: (k: string) => api.send("DELETE", `/api/clients/${clientId}/cfo/targets/${k}`) });
  const { toast } = useToast();
  const applyDefaults = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ added: number }>("POST", `/api/clients/${clientId}/cfo/targets/apply-defaults`),
    onSuccess: (r) => toast({ title: r.added ? `${plural(r.added, "default target")} added` : "No targets added", description: r.added ? undefined : "Every KPI with a firm default already has a target of the client's own, or the firm has no defaults for this industry." }),
  });
  const withTargets = scorecard.filter((k) => k.target);
  const canEdit = hasPermission(me, "clients.write");
  return (
    <Section title="Targets">
      {withTargets.length > 0 && (
        <ul className="mb-3 space-y-1 text-sm">
          {withTargets.map((k) => (
            <li key={k.key} className="flex items-center gap-2">
              <span className="flex-1">
                {k.label}: {k.target!.comparator === "at_least" ? "at least" : "at most"} {targetText(k.key, k.target!.target)}
              </span>
              {canEdit && (
                <button className="text-xs text-muted-foreground underline" onClick={() => remove.mutate(k.key)}>
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <select aria-label="KPI" className={select} value={key} onChange={(e) => setKey(e.target.value as KpiKey)}>
            {KPI_ORDER.map((k) => (
              <option key={k} value={k}>
                {KPI_DEFINITIONS[k].label}
              </option>
            ))}
          </select>
          <select aria-label="Comparison" className={select} value={comparator} onChange={(e) => setComparator(e.target.value as "at_least" | "at_most")}>
            <option value="at_least">at least</option>
            <option value="at_most">at most</option>
          </select>
          <Input aria-label="Target value" className="w-32" placeholder={kind === "percent" ? "40%" : kind === "money" ? "$10,000" : kind === "days" ? "45" : "1.5"} value={value} onChange={(e) => setValue(e.target.value)} />
          <Button size="sm" onClick={() => save.mutate()} disabled={!value.trim() || save.isPending}>
            Set target
          </Button>
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">Amber: within 10% of target. Red: more than 10% off target, which creates a KPI exception.</p>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Button size="sm" variant="outline" onClick={() => applyDefaults.mutate()} disabled={applyDefaults.isPending}>
            Apply the firm's default targets
          </Button>
          <span className="text-xs text-muted-foreground">For the client's industry, from Firm Settings › CFO benchmarks. Targets already set here are kept.</span>
        </div>
      )}
      {applyDefaults.error && <p className="mt-2 text-sm text-destructive">{errorMessage(applyDefaults.error)}</p>}
      {save.error && <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p>}
    </Section>
  );
}

/** A stored target as staff typed it: percentages as percent, money in dollars. */
function targetText(key: KpiKey, target: string): string {
  const kind = KPI_DEFINITIONS[key].kind;
  if (kind === "money") return formatCents(Number(target), { showCents: false });
  if (kind !== "percent") return target;
  const p = Exact.decimal(target).mul(Exact.int(100)).toFixed(4);
  return `${p.replace(/0+$/, "").replace(/\.$/, "")}%`;
}

/** "40" → "0.4", "12.5" → "0.125", done on the text, never through floating point. */
function pctToFraction(p: string): string {
  const neg = p.startsWith("-");
  const [w = "0", f = ""] = p.replace("-", "").split(".");
  const digits = `${w.padStart(3, "0")}${f}`;
  const whole = digits.slice(0, digits.length - 2 - f.length).replace(/^0+(?=\d)/, "") || "0";
  const frac = digits.slice(digits.length - 2 - f.length).replace(/0+$/, "");
  return `${neg ? "-" : ""}${whole}${frac ? `.${frac}` : ""}`;
}

// --- Budget ---------------------------------------------------------------------------------

type Bva = { budget: { name: string; status: string }; preliminaryMonths: string[]; asOf: string; rows: Array<{ accountId: string; label: string; kind: string; month: Variance; ytd: Variance; unbudgeted: boolean }>; totals: Array<{ label: string; kind: string; month: Variance; ytd: Variance }>; netIncome: { month: Variance; ytd: Variance } };

function VarianceCells({ v }: { v: Variance }) {
  const tone = v.favorable === null ? "" : v.favorable ? "text-emerald-700 dark:text-emerald-300" : "text-destructive";
  return (
    <>
      <td className="px-2 py-1 text-right tabular-nums">{formatCents(v.actual)}</td>
      <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">{formatCents(v.budget)}</td>
      <td className={`px-2 py-1 text-right tabular-nums ${tone}`}>
        {v.dollars > 0 ? "+" : ""}
        {formatCents(v.dollars)}{" "}
        <span className="text-xs" title={v.pct.kind === "not_meaningful" ? `${v.pct.reason}. ${NOT_MEANINGFUL_HELP}` : undefined}>
          ({v.pct.display})
        </span>
      </td>
    </>
  );
}

function Budgets({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const list = useQuery<Array<{ id: string; name: string; startMonth: string; status: string }>>({ queryKey: ["/api/clients/", clientId, "/cfo/budgets"] });
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [start, setStart] = useState(`${new Date().getFullYear()}-01`);
  const [seed, setSeed] = useState<"empty" | "last_year">("last_year");
  const [growth, setGrowth] = useState("0");
  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ id: string; lines: number; monthsWithoutHistory: string[] }>("POST", `/api/clients/${clientId}/cfo/budgets`, { name, startMonth: start, seed, growthPercent: growth || "0" }),
    onSuccess: (r) => {
      setSelected(r.id);
      const gaps = r.monthsWithoutHistory ?? [];
      toast({
        title: "Budget created",
        description: `${r.lines} account${r.lines === 1 ? "" : "s"} populated.${gaps.length ? ` Last year has no figures for the months that become ${gaps.map(monthLabel).join(", ")}; those months are $0 until you enter them.` : ""}`,
      });
    },
  });
  const canEdit = hasPermission(me, "clients.write");
  const budgetId = selected ?? list.data?.[0]?.id ?? null;
  return (
    <div className="space-y-6">
      <Section title="Budgets">
        {list.data?.length ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {list.data.map((b) => (
              <Button key={b.id} size="sm" variant={b.id === budgetId ? "default" : "outline"} onClick={() => setSelected(b.id)}>
                {b.name} {b.status === "approved" && <CheckCircle2 className="ml-1 h-3.5 w-3.5" />}
              </Button>
            ))}
          </div>
        ) : (
          <p className="mb-3 text-sm text-muted-foreground">No budgets have been created.</p>
        )}
        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
            <div className="space-y-1">
              <Label htmlFor="b-name">Budget name</Label>
              <Input id="b-name" placeholder="FY 2027" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="b-start">First month</Label>
              <Input id="b-start" type="month" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <select aria-label="Start from" className={select} value={seed} onChange={(e) => setSeed(e.target.value as "empty" | "last_year")}>
              <option value="last_year">Prior-year actuals</option>
              <option value="empty">Blank budget</option>
            </select>
            {seed === "last_year" && <Input aria-label="Growth %" className="w-24" value={growth} onChange={(e) => setGrowth(e.target.value)} placeholder="Growth %" />}
            <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
              Create
            </Button>
          </div>
        )}
        {create.error && <p className="mt-2 text-sm text-destructive">{errorMessage(create.error)}</p>}
      </Section>
      {budgetId && <BudgetDetail key={budgetId} id={budgetId} me={me} />}
    </div>
  );
}

function BudgetDetail({ id, me }: { id: string; me: Me }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const b = useQuery<{ name: string; startMonth: string; status: string; lines: Array<{ accountId: string; amounts: number[] }>; accounts: Array<{ qboId: string; fqn: string }> }>({ queryKey: ["/api/cfo/budgets/", id] });
  const months = b.data ? Array.from({ length: 12 }, (_, i) => shift(b.data!.startMonth, i)) : [];
  const [month, setMonth] = useState<string | null>(null);
  const m = month ?? months.find((x) => x <= new Date().toISOString().slice(0, 7) && shift(x, 1) > new Date(Date.now() - 31 * 86400000).toISOString().slice(0, 7)) ?? months[0];
  const bva = useQuery<Bva>({ queryKey: [`/api/cfo/budgets/${id}/vs-actual?month=${m}`], enabled: !!m, retry: false });
  const [csv, setCsv] = useState("");
  const importCsv = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ imported: number; errors: Array<{ line: number; message: string }> }>("POST", `/api/cfo/budgets/${id}/import`, { csv }),
    onSuccess: (r) => {
      setCsv("");
      toast({ title: `Imported ${r.imported} account${r.imported === 1 ? "" : "s"}` });
    },
  });
  const approve = useMutation({ mutationFn: () => api.send("POST", `/api/cfo/budgets/${id}/approve`) });
  if (!b.data) return <Loading />;
  const importErrors = importCsv.error instanceof ApiError ? (importCsv.error.details as unknown) : null;
  const errs = (importCsv.error as ApiError & { body?: unknown } | null) && importCsv.isError ? ((importCsv.error as unknown as { errors?: Array<{ line: number; message: string }> }).errors ?? null) : null;
  void importErrors;
  void errs;
  const editable = b.data.status !== "approved" && hasPermission(me, "clients.write");
  return (
    <Section
      title={`${b.data.name} — budget vs actual`}
      action={
        <div className="flex items-center gap-2">
          <select aria-label="Month" className={select} value={m} onChange={(e) => setMonth(e.target.value)}>
            {months.map((x) => (
              <option key={x} value={x}>
                {monthLabel(x)}
              </option>
            ))}
          </select>
          {editable && hasPermission(me, "engagements.write") && (
            <Button size="sm" variant="outline" onClick={async () => (await ask({ title: "Approve this budget?", description: "An approved budget cannot be edited.", confirmLabel: "Approve budget" })) && approve.mutate()}>
              Approve
            </Button>
          )}
        </div>
      }
    >
      {bva.isLoading ? (
        <Loading />
      ) : bva.error || !bva.data ? (
        <p className="text-sm text-destructive">{errorMessage(bva.error)}</p>
      ) : (
        <TableScroll label="Budget against actual table">
          {bva.data.preliminaryMonths.length > 0 && <p className="mb-2 text-xs text-amber-700">Preliminary: books aren't closed for {bva.data.preliminaryMonths.map(monthLabel).join(", ")}.</p>}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="py-1 text-left">Account</th>
                <th className="px-2 text-right">Actual</th>
                <th className="px-2 text-right">Budget</th>
                <th className="px-2 text-right">Variance</th>
                <th className="px-2 text-right">YTD actual</th>
                <th className="px-2 text-right">YTD budget</th>
                <th className="px-2 text-right">YTD variance</th>
              </tr>
            </thead>
            <tbody>
              {bva.data.rows.map((r) => (
                <tr key={r.accountId} className="border-t border-border">
                  <td className="py-1">
                    {r.label}
                    {r.unbudgeted && <span className="ml-1 text-xs text-muted-foreground">(unbudgeted)</span>}
                  </td>
                  <VarianceCells v={r.month} />
                  <VarianceCells v={r.ytd} />
                </tr>
              ))}
              {bva.data.totals.map((r) => (
                <tr key={r.kind} className="border-t border-border font-medium">
                  <td className="py-1">{r.label}</td>
                  <VarianceCells v={r.month} />
                  <VarianceCells v={r.ytd} />
                </tr>
              ))}
              <tr className="border-t-2 border-border font-semibold">
                <td className="py-1">Net income</td>
                <VarianceCells v={bva.data.netIncome.month} />
                <VarianceCells v={bva.data.netIncome.ytd} />
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">
            <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-600" />
            Actual net income matches QuickBooks for every month through {monthLabel(m!)}. As of {formatDateTime(bva.data.asOf)}.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Variance is actual minus budget, with the percentage of the budget in brackets. {NOT_MEANINGFUL_HELP}</p>
        </TableScroll>
      )}
      {editable && (
        <div className="mt-4 space-y-2 border-t border-border pt-3">
          <Label htmlFor="b-csv">Import budget lines from CSV</Label>
          <p className="text-xs text-muted-foreground">First column: the account's full name as it appears in QuickBooks, followed by twelve monthly amounts. Enclose amounts containing commas in quotes. All lines are validated before anything is saved.</p>
          <Textarea id="b-csv" rows={4} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={`Account,Jan,Feb,...\nSales,"20,000.00",...`} />
          <Button size="sm" onClick={() => importCsv.mutate()} disabled={!csv.trim() || importCsv.isPending}>
            Import
          </Button>
          {importCsv.error && <p className="text-sm text-destructive">{errorMessage(importCsv.error)}</p>}
          <p className="text-xs text-muted-foreground">{b.data.lines.length} account{b.data.lines.length === 1 ? "" : "s"} budgeted.</p>
        </div>
      )}
      <button className="hidden" onClick={() => qc.invalidateQueries()} />
    </Section>
  );
}

function shift(month: string, k: number): string {
  const [y, mo] = month.split("-").map(Number) as [number, number];
  const idx = y * 12 + (mo - 1) + k;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

// --- Forecasts ------------------------------------------------------------------------------

type Assumptions = { monthlyGrowthPct: string; cogsSharePointsChange: string; priceChanges: Array<{ month: string; pct: string }>; newHires: Array<{ label: string; startMonth: string; monthlyCost: number }>; oneTime: Array<{ label: string; month: string; amount: number; kind: "income" | "expense" }>; loans: Array<{ label: string; month: string; proceeds: number; monthlyPayment: number; payments: number }> };
type PnlMonth = { month: string; revenue: number; cogs: number; grossProfit: number; operatingExpenses: number; otherNet: number; netIncome: number; cashChange: number; cashEnd: number };
type PnlForecast = { months: PnlMonth[]; totals: Omit<PnlMonth, "month" | "cashEnd">; runwayMonths: number | null; firstNegativeMonth: string | null };
type ForecastsView = {
  pnl12: { version: number; assumptions: Record<string, Assumptions>; output: { scenarios: Record<string, PnlForecast> }; firstPeriod: string; createdAt: string } | null;
  cash13: { version: number; output: { weeks: Array<{ week: number; start: string; opening: number; net: number; closing: number }> }; firstPeriod: string; createdAt: string } | null;
  accuracy: Array<{ version: number; months: Array<{ month: string; revenue: { forecast: number; actual: number; dollars: number; pct: string }; netIncome: { forecast: number; actual: number; dollars: number; pct: string } }> }>;
  defaults: {
    firstMonth: string;
    startDate: string;
    scenarios: Record<string, Assumptions>;
    cash: Record<string, unknown> & { payroll: { amountPerRun: number; frequency: string; nextPayDate: string; semimonthlyDays?: [number, number] } | null };
    payrollSource: { scheduleName: string; lastPayDate: string } | null;
  };
};

function Forecasts({ clientId, me }: { clientId: string; me: Me }) {
  const { data } = useQuery<ForecastsView>({ queryKey: ["/api/clients/", clientId, "/cfo/forecasts"] });
  if (!data) return <Loading />;
  return (
    <div className="space-y-6">
      <PnlForecastPanel clientId={clientId} me={me} data={data} />
      <CashForecastPanel clientId={clientId} me={me} data={data} />
      {data.accuracy.length > 0 && (
        <Section title="Forecast accuracy (closed months)">
          {data.accuracy.map((v) => (
            <div key={v.version} className="mb-3">
              <p className="text-xs text-muted-foreground">Version {v.version}</p>
              <table className="w-full text-sm">
                <tbody>
                  {v.months.map((m) => (
                    <tr key={m.month} className="border-t border-border">
                      <td className="py-1">{monthLabel(m.month)}</td>
                      <td className="px-2 text-right">Revenue: forecast {formatCents(m.revenue.forecast)}, actual {formatCents(m.revenue.actual)} ({m.revenue.pct})</td>
                      <td className="px-2 text-right">Net income: forecast {formatCents(m.netIncome.forecast)}, actual {formatCents(m.netIncome.actual)} ({m.netIncome.pct})</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function PnlForecastPanel({ clientId, me, data }: { clientId: string; me: Me; data: ForecastsView }) {
  const { toast } = useToast();
  const saved = data.pnl12;
  const [firstMonth, setFirstMonth] = useState(saved?.firstPeriod ?? data.defaults.firstMonth);
  const [growth, setGrowth] = useState(saved?.assumptions.base?.monthlyGrowthPct ?? "0");
  const [cogsPts, setCogsPts] = useState(saved?.assumptions.base?.cogsSharePointsChange ?? "0");
  const [hire, setHire] = useState({ label: "", startMonth: "", cost: "" });
  const [hires, setHires] = useState<Assumptions["newHires"]>(saved?.assumptions.base?.newHires ?? []);
  const [scenario, setScenario] = useState("base");
  const [preview, setPreview] = useState<{ scenarios: Record<string, PnlForecast>; baseline: Record<string, unknown>; baselineMonths: string[]; baselineNotClosed?: string[] } | null>(null);
  const build = (save: boolean) => {
    const base: Assumptions = { monthlyGrowthPct: growth || "0", cogsSharePointsChange: cogsPts || "0", priceChanges: [], newHires: hires, oneTime: [], loans: [] };
    const shiftPct = (s2: string, by: number) => String(Math.round((Number(s2) + by) * 100) / 100);
    const scenarios = { base, best: { ...base, monthlyGrowthPct: shiftPct(base.monthlyGrowthPct, 1), cogsSharePointsChange: shiftPct(base.cogsSharePointsChange, -1) }, worst: { ...base, monthlyGrowthPct: shiftPct(base.monthlyGrowthPct, -2), cogsSharePointsChange: shiftPct(base.cogsSharePointsChange, 2) } };
    return api.send<{ scenarios: Record<string, PnlForecast>; baseline: Record<string, unknown>; baselineMonths: string[]; baselineNotClosed?: string[] }>("POST", `/api/clients/${clientId}/cfo/forecasts/pnl12`, { firstMonth, scenarios, save });
  };
  const run = useMutation({ meta: { inlineError: true }, mutationFn: (save: boolean) => build(save), onSuccess: (r, save) => {
    setPreview(r);
    if (save) toast({ title: "Forecast saved as a new version" });
  } });
  const shown = preview?.scenarios ?? saved?.output.scenarios ?? null;
  const f = shown?.[scenario];
  const canRun = hasPermission(me, "engagements.write");
  return (
    <Section title="12-month P&L forecast" action={saved && <span className="text-xs text-muted-foreground">Saved version {saved.version}, {formatDate(saved.createdAt)}</span>}>
      {canRun && (
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="f-first">First month</Label>
            <Input id="f-first" type="month" value={firstMonth} onChange={(e) => setFirstMonth(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f-growth">Monthly growth %</Label>
            <Input id="f-growth" className="w-24" value={growth} onChange={(e) => setGrowth(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f-cogs">COGS % change (points)</Label>
            <Input id="f-cogs" className="w-24" value={cogsPts} onChange={(e) => setCogsPts(e.target.value)} />
          </div>
          <Button variant="outline" onClick={() => run.mutate(false)} disabled={run.isPending}>
            Preview
          </Button>
          <Button onClick={() => run.mutate(true)} disabled={run.isPending}>
            Save version
          </Button>
        </div>
      )}
      {canRun && (
        <div className="mb-4 flex flex-wrap items-end gap-2 text-sm">
          <span className="text-xs text-muted-foreground">Planned hire:</span>
          <Input aria-label="Role" className="w-36" placeholder="Role" value={hire.label} onChange={(e) => setHire({ ...hire, label: e.target.value })} />
          <Input aria-label="Start month" type="month" className="w-36" value={hire.startMonth} onChange={(e) => setHire({ ...hire, startMonth: e.target.value })} />
          <Input aria-label="Monthly cost" className="w-32" placeholder="Monthly cost ($)" value={hire.cost} onChange={(e) => setHire({ ...hire, cost: e.target.value })} />
          <Button size="sm" variant="ghost" disabled={!hire.label || !hire.startMonth || !hire.cost} onClick={() => {
            try {
              setHires([...hires, { label: hire.label, startMonth: hire.startMonth, monthlyCost: parseDollars(hire.cost) }]);
              setHire({ label: "", startMonth: "", cost: "" });
            } catch {
              toast({ title: "Enter the monthly cost in dollars, for example 4,500.00", variant: "destructive" });
            }
          }}>
            Add
          </Button>
          {hires.map((h, i) => (
            <span key={i} className="rounded bg-muted px-2 py-0.5 text-xs">
              {h.label} from {monthLabel(h.startMonth)}: {formatCents(h.monthlyCost)}/mo{" "}
              <button aria-label="Remove planned hire" onClick={() => setHires(hires.filter((_, j) => j !== i))}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {run.error && <p className="mb-3 text-sm text-destructive">{errorMessage(run.error)}</p>}
      {!f ? (
        <Empty>Select Preview to build the forecast. It starts from the average of the three months before the first forecast month, taken from QuickBooks.</Empty>
      ) : (
        <>
          {preview?.baselineNotClosed && preview.baselineNotClosed.length > 0 && (
            <p className="mb-2 text-xs text-amber-700">Baseline months not yet closed: {preview.baselineNotClosed.map(monthLabel).join(", ")}. Their actuals may still change.</p>
          )}
          <p className="mb-2 text-xs text-muted-foreground">
            Cash is profit plus depreciation, plus loan proceeds, less loan payments. It leaves out changes in receivables and payables, equipment purchases, owner draws and payments on existing loans.
          </p>
          <div className="mb-3 flex gap-2">
            {["base", "best", "worst"].map((s2) => (
              <Button key={s2} size="sm" variant={s2 === scenario ? "default" : "outline"} onClick={() => setScenario(s2)}>
                {s2[0]!.toUpperCase() + s2.slice(1)}
              </Button>
            ))}
          </div>
          <div className="mb-3 text-xs">
            <p className="font-medium text-muted-foreground">{FORECAST_RUNWAY_LABEL}</p>
            <dl className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-3">
              {(["base", "best", "worst"] as const).map((s2) => (
                <div key={s2} className="flex min-w-0 gap-1">
                  <dt className="text-muted-foreground">{s2 === "base" ? "Base" : s2 === "best" ? "Best" : "Worst"}:</dt>
                  <dd className="tabular font-medium">{monthsUntilCashRunsOut(shown![s2]!.months).display}</dd>
                </div>
              ))}
            </dl>
          </div>
          <TableScroll label="Profit and loss forecast by month table">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="py-1 text-left">
                    <span className="sr-only">Line</span>
                  </th>
                  {f.months.map((m) => (
                    <th key={m.month} className="px-1.5 text-right">
                      {monthLabel(m.month)}
                    </th>
                  ))}
                  <th className="px-1.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ["Revenue", "revenue"],
                    ["Cost of goods sold", "cogs"],
                    ["Gross profit", "grossProfit"],
                    ["Operating expenses", "operatingExpenses"],
                    ["Net income", "netIncome"],
                    ["Net change in cash", "cashChange"],
                  ] as const
                ).map(([label, k]) => (
                  <tr key={k} className="border-t border-border">
                    <td className="py-1 pr-2">{label}</td>
                    {f.months.map((m) => (
                      <td key={m.month} className="px-1.5 text-right tabular-nums">
                        {formatCents(m[k])}
                      </td>
                    ))}
                    <td className="px-1.5 text-right font-medium tabular-nums">{formatCents(f.totals[k])}</td>
                  </tr>
                ))}
                <tr className="border-t border-border font-medium">
                  <td className="py-1 pr-2">Ending cash</td>
                  {f.months.map((m) => (
                    <td key={m.month} className={`px-1.5 text-right tabular-nums ${m.cashEnd < 0 ? "text-destructive" : ""}`}>
                      {formatCents(m.cashEnd)}
                    </td>
                  ))}
                  <td />
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p className="mt-2 text-xs text-muted-foreground">
            A forecast is an estimate. Best and worst shift monthly growth by +1 / −2 points and cost of goods by −1 / +2 points from base.
            {` ${FORECAST_RUNWAY_LABEL} in this scenario: ${monthsUntilCashRunsOut(f.months).display}.`}
          </p>
        </>
      )}
    </Section>
  );
}

function CashForecastPanel({ clientId, me, data }: { clientId: string; me: Me; data: ForecastsView }) {
  const { toast } = useToast();
  // Prefilled from the payroll service when the client has a pay schedule and a processed run.
  const fromPayroll = data.defaults.cash.payroll;
  const [payroll, setPayroll] = useState(fromPayroll ? { amount: (fromPayroll.amountPerRun / 100).toFixed(2), frequency: fromPayroll.frequency, next: fromPayroll.nextPayDate } : { amount: "", frequency: "biweekly", next: "" });
  const [preview, setPreview] = useState<{ weeks: Array<{ week: number; start: string; opening: number; net: number; closing: number; inflows: Array<{ label: string; amount: number }>; outflows: Array<{ label: string; amount: number }> }>; lowestClosing: number; lowestWeek: number; openingCash: number } | null>(null);
  const run = useMutation({
    meta: { inlineError: true },
    mutationFn: (save: boolean) =>
      api.send<NonNullable<typeof preview>>("POST", `/api/clients/${clientId}/cfo/forecasts/cash13`, {
        startDate: data.defaults.startDate,
        assumptions: { ...data.defaults.cash, payroll: payroll.amount && payroll.next ? { amountPerRun: cents(payroll.amount, "Payroll per run"), frequency: payroll.frequency, nextPayDate: payroll.next, ...(payroll.frequency === "semimonthly" && fromPayroll?.semimonthlyDays ? { semimonthlyDays: fromPayroll.semimonthlyDays } : {}) } : null },
        save,
      }),
    onSuccess: (r, save) => {
      setPreview(r);
      if (save) toast({ title: "Cash forecast saved" });
    },
  });
  const canRun = hasPermission(me, "engagements.write");
  return (
    <Section title="13-week cash forecast">
      {canRun && (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Input aria-label="Payroll per run" className="w-36" placeholder="Payroll per run ($)" value={payroll.amount} onChange={(e) => setPayroll({ ...payroll, amount: e.target.value })} />
          <select aria-label="Pay frequency" className={select} value={payroll.frequency} onChange={(e) => setPayroll({ ...payroll, frequency: e.target.value })}>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="semimonthly">Semimonthly</option>
            <option value="monthly">Monthly</option>
          </select>
          <Input aria-label="Next pay date" type="date" className="w-40" value={payroll.next} onChange={(e) => setPayroll({ ...payroll, next: e.target.value })} />
          <Button variant="outline" onClick={() => run.mutate(false)} disabled={run.isPending}>
            Preview
          </Button>
          <Button onClick={() => run.mutate(true)} disabled={run.isPending}>
            Save version
          </Button>
        </div>
      )}
      {canRun && data.defaults.payrollSource && (
        <p className="mb-3 text-xs text-muted-foreground">
          Payroll is filled in from the payroll service: the “{data.defaults.payrollSource.scheduleName}” pay schedule and the gross pay plus employer taxes of the run paid {formatDate(data.defaults.payrollSource.lastPayDate)}. Change it here if the next runs will differ.
        </p>
      )}
      {run.error && <p className="mb-2 text-sm text-destructive">{errorMessage(run.error)}</p>}
      {!preview ? (
        <Empty>Starts from the current bank balance and the receivables and payables aging in QuickBooks. Collection assumptions: current 100% over weeks 1–5; 1–30 days 95%; 31–60 days 80%; 61–90 days 50%; over 90 days 0%.</Empty>
      ) : (
        <TableScroll label="13-week cash forecast table">
          <p className="mb-2 text-sm">
            Opening cash {formatCents(preview.openingCash)}. Lowest point {formatCents(preview.lowestClosing)} in week {preview.lowestWeek}.
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="py-1 text-left">Week of</th>
                <th className="px-2 text-right">In</th>
                <th className="px-2 text-right">Outflows</th>
                <th className="px-2 text-right">Ending cash</th>
              </tr>
            </thead>
            <tbody>
              {preview.weeks.map((w) => (
                <tr key={w.week} className="border-t border-border">
                  <td className="py-1">{formatDate(w.start)}</td>
                  <td className="px-2 text-right tabular-nums" title={w.inflows.map((x) => `${x.label}: ${formatCents(x.amount)}`).join("\n")}>
                    {formatCents(w.inflows.reduce((s2, x) => s2 + x.amount, 0))}
                  </td>
                  <td className="px-2 text-right tabular-nums" title={w.outflows.map((x) => `${x.label}: ${formatCents(x.amount)}`).join("\n")}>
                    {formatCents(w.outflows.reduce((s2, x) => s2 + x.amount, 0))}
                  </td>
                  <td className={`px-2 text-right tabular-nums ${w.closing < 0 ? "text-destructive" : ""}`}>{formatCents(w.closing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Section>
  );
}

// --- Monthly report -------------------------------------------------------------------------

type Report = { id: string; period: string; status: "draft" | "approved" | "published"; facts: Array<{ id: string; label: string; value: string }>; sections: Array<{ heading: string; body: string }>; aiDrafted: boolean; aiModel: string | null; unmatched: string[]; documentId: string | null; aiNote?: string | null };

function MonthlyReport({ clientId, me, snapshots }: { clientId: string; me: Me; snapshots: CfoView["snapshots"] }) {
  const qc = useQueryClient();
  const list = useQuery<Array<{ id: string; period: string; status: string }>>({ queryKey: ["/api/clients/", clientId, "/cfo/reports"] });
  const [period, setPeriod] = useState(snapshots[snapshots.length - 1]?.period ?? "");
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const start = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<Report>("POST", `/api/clients/${clientId}/cfo/reports`, { period }),
    onSuccess: (r) => {
      setOpenId(r.id);
      setNote(r.aiNote ?? null);
      qc.setQueryData(["/api/cfo/reports/", r.id], r);
    },
  });
  const canEdit = hasPermission(me, "engagements.write");
  const current = openId ?? list.data?.[0]?.id ?? null;
  return (
    <div className="space-y-6">
      <Section title="Monthly CFO Report">
        <div className="flex flex-wrap items-end gap-2">
          {canEdit && (
            <>
              <select aria-label="Month" className={select} value={period} onChange={(e) => setPeriod(e.target.value)}>
                {snapshots.map((sn) => (
                  <option key={sn.period} value={sn.period}>
                    {monthLabel(sn.period)}
                    {sn.quality === "preliminary" ? " (preliminary)" : ""}
                  </option>
                ))}
              </select>
              <Button onClick={() => start.mutate()} disabled={!period || start.isPending}>
                {start.isPending ? "Drafting…" : "Draft report"}
              </Button>
            </>
          )}
          {list.data?.map((r) => (
            <Button key={r.id} size="sm" variant={r.id === current ? "default" : "outline"} onClick={() => setOpenId(r.id)}>
              {monthLabel(r.period)} · {r.status}
            </Button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Commentary is drafted automatically from the calculated figures only. Staff review and approve it before it is shared with the client.</p>
        {start.error && <p className="mt-2 text-sm text-destructive">{errorMessage(start.error)}</p>}
        {note && <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:bg-amber-900/20">{note}</p>}
      </Section>
      {current && <ReportEditor key={current} id={current} me={me} />}
    </div>
  );
}

function ReportEditor({ id, me }: { id: string; me: Me }) {
  const { toast } = useToast();
  const { data } = useQuery<Report>({ queryKey: ["/api/cfo/reports/", id] });
  const [sections, setSections] = useState<Report["sections"] | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<Report>("PUT", `/api/cfo/reports/${id}`, { sections }), onSuccess: () => setSections(null) });
  const approve = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/cfo/reports/${id}/approve`, { confirmNumbers: confirmed ? data!.unmatched : [] }) });
  const publish = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/cfo/reports/${id}/publish`), onSuccess: () => toast({ title: "Published to the client portal" }) });
  if (!data) return <Loading />;
  const editing = sections ?? data.sections;
  const editable = data.status !== "published" && hasPermission(me, "engagements.write");
  const dirty = sections !== null;
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Section title={`Commentary — ${monthLabel(data.period)}`} className="lg:col-span-2">
        {data.aiDrafted && <p className="mb-3 text-xs text-muted-foreground">Drafted by AI ({data.aiModel}) from the figures on the right. Every number was filled in by ClientFlow, not typed by the AI.</p>}
        <div className="space-y-4">
          {editing.map((s, i) => (
            <div key={i} className="space-y-1">
              <Input aria-label={`Heading ${i + 1}`} value={s.heading} disabled={!editable} onChange={(e) => setSections(editing.map((x, j) => (j === i ? { ...x, heading: e.target.value } : x)))} />
              <Textarea aria-label={`Text ${i + 1}`} rows={4} value={s.body} disabled={!editable} onChange={(e) => setSections(editing.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} />
            </div>
          ))}
          {editable && (
            <Button size="sm" variant="ghost" onClick={() => setSections([...editing, { heading: "", body: "" }])}>
              Add section
            </Button>
          )}
        </div>
        {editable && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {dirty && (
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                Save changes
              </Button>
            )}
            {!dirty && data.status === "draft" && (
              <>
                {data.unmatched.length > 0 && (
                  <label className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
                    <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                    I have verified these figures, which do not match a calculated figure: {data.unmatched.join(", ")}
                  </label>
                )}
                <Button onClick={() => approve.mutate()} disabled={!editing.length || approve.isPending || (data.unmatched.length > 0 && !confirmed)}>
                  Approve
                </Button>
              </>
            )}
            <Button asChild variant="outline">
              <a href={`/api/cfo/reports/${id}/preview`} target="_blank" rel="noreferrer">
                Preview PDF
              </a>
            </Button>
            {data.status === "approved" && (
              <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
                Publish to client
              </Button>
            )}
          </div>
        )}
        {data.status === "published" && <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">Published. The PDF is saved in the client's CFO Reports folder.</p>}
        {(save.error || approve.error || publish.error) && <p className="mt-2 text-sm text-destructive">{errorMessage(save.error ?? approve.error ?? publish.error)}</p>}
      </Section>
      <Section title="Reference figures" description={`Calculated figures available to the commentary. Unlabeled figures are for ${monthLabel(data.period)}.`}>
        <ul tabIndex={0} aria-label="Reference figures list" className="max-h-128 divide-y divide-border overflow-y-auto rounded-sm text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
          {data.facts.map((f) => (
            <li key={f.id} className="flex gap-3 py-1.5">
              <span className="flex-1 text-muted-foreground">{f.label.replace(`, ${monthLabel(data.period)} (preliminary)`, "").replace(`, ${monthLabel(data.period)}`, "")}</span>
              <span className="tabular-nums font-medium">{f.value}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

// --- Meetings -------------------------------------------------------------------------------

type MeetingsView = {
  meetings: Array<{ id: string; title: string; meetingOn: string; status: string; agendaKpis: KpiKey[]; agendaNotes: string | null; notes: string | null }>;
  actions: Array<{ id: string; title: string; owner: "firm" | "client"; dueOn: string | null; kpiKey: KpiKey | null; status: string; meetingId: string | null }>;
  kpiNow: Partial<Record<KpiKey, { display: string; period: string; quality: string }>>;
};

function Meetings({ clientId, me }: { clientId: string; me: Me }) {
  const { data } = useQuery<MeetingsView>({ queryKey: ["/api/clients/", clientId, "/cfo/meetings"] });
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [agenda, setAgenda] = useState<KpiKey[]>([]);
  const create = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/clients/${clientId}/cfo/meetings`, { title, meetingOn: date, agendaKpis: agenda }), onSuccess: () => { setTitle(""); setDate(""); setAgenda([]); } });
  const [action, setAction] = useState({ title: "", owner: "client" as "firm" | "client", dueOn: "", kpiKey: "" });
  const addAction = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/clients/${clientId}/cfo/actions`, { title: action.title, owner: action.owner, ...(action.dueOn ? { dueOn: action.dueOn } : {}), ...(action.kpiKey ? { kpiKey: action.kpiKey } : {}) }),
    onSuccess: () => setAction({ title: "", owner: "client", dueOn: "", kpiKey: "" }),
  });
  const setStatus = useMutation({ mutationFn: (v: { id: string; status: string }) => api.send("PATCH", `/api/cfo/actions/${v.id}`, { status: v.status }) });
  const setMeeting = useMutation({ mutationFn: (v: { id: string; status: string }) => api.send("PATCH", `/api/cfo/meetings/${v.id}`, { status: v.status }) });
  const canEdit = hasPermission(me, "engagements.write");
  if (!data) return <Loading />;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section title="Meetings">
        {canEdit && (
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap gap-2">
              <Input aria-label="Meeting title" placeholder="e.g. January financial review" value={title} onChange={(e) => setTitle(e.target.value)} className="flex-1" />
              <Input aria-label="Meeting date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
            </div>
            <div className="flex flex-wrap gap-1">
              {KPI_ORDER.map((k) => (
                <button key={k} className={`rounded-full border px-2 py-0.5 text-xs ${agenda.includes(k) ? "border-primary bg-primary/10" : "border-border"}`} onClick={() => setAgenda(agenda.includes(k) ? agenda.filter((x) => x !== k) : [...agenda, k])}>
                  {KPI_DEFINITIONS[k].label}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={() => create.mutate()} disabled={!title.trim() || !date || create.isPending}>
              Plan meeting
            </Button>
            {create.error && <p className="text-sm text-destructive">{errorMessage(create.error)}</p>}
          </div>
        )}
        {!data.meetings.length ? (
          <Empty>No meetings have been scheduled.</Empty>
        ) : (
          <ul className="space-y-3">
            {data.meetings.map((m) => (
              <li key={m.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2">
                  <p className="flex-1 font-medium">{m.title}</p>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(m.meetingOn)} · {m.status}
                  </span>
                  {canEdit && m.status === "planned" && (
                    <Button size="sm" variant="ghost" onClick={() => setMeeting.mutate({ id: m.id, status: "held" })}>
                      Mark held
                    </Button>
                  )}
                </div>
                {m.agendaKpis.length > 0 && (
                  <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                    {m.agendaKpis.map((k) => (
                      <li key={k} className="flex justify-between gap-2 rounded bg-muted/50 px-2 py-1">
                        <span>{KPI_DEFINITIONS[k].label}</span>
                        <span className="tabular-nums">{data.kpiNow[k]?.display ?? "—"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Action items">
        {canEdit && (
          <div className="mb-4 flex flex-wrap items-end gap-2">
            <Input aria-label="Action item" placeholder="Action item" value={action.title} onChange={(e) => setAction({ ...action, title: e.target.value })} className="flex-1" />
            <select aria-label="Owner" className={select} value={action.owner} onChange={(e) => setAction({ ...action, owner: e.target.value as "firm" | "client" })}>
              <option value="client">Client</option>
              <option value="firm">Firm</option>
            </select>
            <Input aria-label="Due date" type="date" className="w-40" value={action.dueOn} onChange={(e) => setAction({ ...action, dueOn: e.target.value })} />
            <select aria-label="Linked KPI" className={select} value={action.kpiKey} onChange={(e) => setAction({ ...action, kpiKey: e.target.value })}>
              <option value="">No KPI</option>
              {KPI_ORDER.map((k) => (
                <option key={k} value={k}>
                  {KPI_DEFINITIONS[k].label}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={() => addAction.mutate()} disabled={!action.title.trim() || addAction.isPending}>
              Add
            </Button>
          </div>
        )}
        {!data.actions.length ? (
          <Empty>No open action items.</Empty>
        ) : (
          <ul className="divide-y divide-border">
            {data.actions.map((a) => (
              <li key={a.id} className="flex items-center gap-2 py-2 text-sm">
                <button aria-label={a.status === "done" ? "Reopen" : "Mark complete"} disabled={!canEdit} onClick={() => setStatus.mutate({ id: a.id, status: a.status === "done" ? "open" : "done" })}>
                  {a.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                </button>
                <span className={`flex-1 ${a.status !== "open" ? "text-muted-foreground line-through" : ""}`}>{a.title}</span>
                <span className="text-xs text-muted-foreground">
                  {a.owner === "client" ? "Client" : "Firm"}
                  {a.dueOn && ` · due ${formatDate(a.dueOn)}`}
                  {a.kpiKey && ` · ${KPI_DEFINITIONS[a.kpiKey].label}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// --- Statement check ------------------------------------------------------------------------

const SECTIONS = ["revenue", "cogs", "operating_expense", "other_income", "other_expense", "net_income", "cash", "accounts_receivable", "inventory", "other_current_asset", "fixed_asset", "other_asset", "accounts_payable", "other_current_liability", "long_term_liability", "equity", "subtotal"];

function StatementCheck({ clientId, me }: { clientId: string; me: Me }) {
  const [analysis, setAnalysis] = useState<{ id: string; lines: Array<{ label: string; amount: string; section: string }>; periodStart: string | null; periodEnd: string | null } | null>(null);
  const [results, setResults] = useState<Record<string, { label: string; display: string; note: string | null }> | null>(null);
  const upload = useMutation({
    meta: { inlineError: true },
    mutationFn: (f: File) => api.upload<NonNullable<typeof analysis>>(`/api/clients/${clientId}/cfo/statements`, {}, f),
    onSuccess: (r) => {
      setAnalysis(r);
      setResults(null);
    },
  });
  const verify = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      await api.send("PUT", `/api/cfo/statements/${analysis!.id}`, { lines: analysis!.lines, periodStart: analysis!.periodStart, periodEnd: analysis!.periodEnd });
      return api.send<{ results: Record<string, { label: string; display: string; note: string | null }> }>("POST", `/api/cfo/statements/${analysis!.id}/verify`);
    },
    onSuccess: (r) => setResults(r.results),
  });
  if (!hasPermission(me, "documents.write")) return <Empty>You do not have permission to upload statements.</Empty>;
  return (
    <Section title="Financial statement analysis (uploaded income statement or balance sheet)">
      <p className="mb-3 text-sm text-muted-foreground">Upload a PDF (extracted automatically) or a CSV with the columns line, amount and section. Check each line against the source document, then verify. Ratios are calculated only after verification and only if the printed totals tie out.</p>
      <UploadButton label="Upload statement" disabled={upload.isPending} onFile={(f) => upload.mutate(f)} />
      {upload.error && <p className="mt-2 text-sm text-destructive">{errorMessage(upload.error)}</p>}
      {analysis && !results && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="space-y-1">
              <Label htmlFor="st-from">Period from</Label>
              <Input id="st-from" type="date" value={analysis.periodStart ?? ""} onChange={(e) => setAnalysis({ ...analysis, periodStart: e.target.value || null })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="st-to">to</Label>
              <Input id="st-to" type="date" value={analysis.periodEnd ?? ""} onChange={(e) => setAnalysis({ ...analysis, periodEnd: e.target.value || null })} />
            </div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {analysis.lines.map((l, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1 pr-2">
                    <Input aria-label={`Line ${i + 1} label`} value={l.label} onChange={(e) => setAnalysis({ ...analysis, lines: analysis.lines.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                  </td>
                  <td className="py-1 pr-2">
                    <Input aria-label={`Line ${i + 1} amount`} className="w-32 text-right" value={l.amount} onChange={(e) => setAnalysis({ ...analysis, lines: analysis.lines.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)) })} />
                  </td>
                  <td className="py-1">
                    <select aria-label={`Line ${i + 1} section`} className={select} value={l.section} onChange={(e) => setAnalysis({ ...analysis, lines: analysis.lines.map((x, j) => (j === i ? { ...x, section: e.target.value } : x)) })}>
                      {SECTIONS.map((s2) => (
                        <option key={s2} value={s2}>
                          {s2.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => verify.mutate()} disabled={verify.isPending}>
            I checked every line — verify and analyze
          </Button>
          {verify.error && <p className="text-sm text-destructive">{errorMessage(verify.error)}</p>}
        </div>
      )}
      {results && (
        <ul className="mt-4 grid gap-1 text-sm sm:grid-cols-2">
          {Object.entries(results).map(([k, v]) => (
            <li key={k} className="flex justify-between gap-2 rounded bg-muted/50 px-2 py-1" title={v.note ?? undefined}>
              <span>{v.label}</span>
              <span className="tabular-nums">{v.display}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
